import React, { useEffect, useRef, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContexte';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage, getLocale } from '../../contexts/LanguageContext';
import api from '../../api';
import useServerZones from '../../hooks/useServerZones';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import './PreferencesPage.css';

const formatDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return {
        date: d.toLocaleDateString(getLocale(), { day: '2-digit', month: 'long', year: 'numeric' }),
        time: d.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' })
    };
};

/**
 * Classement des zones par ordre de préférence (utilisé pour l'attribution des fiches de réservation).
 * Les zones privatisées (BDA) n'apparaissent pas.
 */
const PreferencesPage = () => {
    const { serverId } = useParams();
    const { user } = useAuth();
    const { t } = useLanguage();
    const { server } = useOutletContext() || {};
    const { success, error: showError } = useNotification();
    const { availableZones: serverZones, loading: zonesLoading } = useServerZones(serverId);

    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [dragging, setDragging] = useState(null);   // index réel en cours de glissement
    const [dragOver, setDragOver] = useState(null);
    const dragRef = useRef(null);
    const [savedAt, setSavedAt] = useState(null);
    const [hasSaved, setHasSaved] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [rankEdit, setRankEdit] = useState(null);   // { realIdx, value }

    /* ── Ordre enregistré, complété par les zones du serveur pas encore classées ── */
    useEffect(() => {
        if (!user || zonesLoading) return;
        const byName = new Map(serverZones.map(z => [z.zone, z]));
        api.get(`/zone-preferences/${serverId}`)
            .then(res => {
                if (res.data?.preferences?.length > 0) {
                    // Le donjon suit la liste du serveur ; les zones retirées disparaissent
                    const saved = res.data.preferences.filter(p => byName.has(p.zone)).map(p => byName.get(p.zone));
                    const savedNames = new Set(saved.map(z => z.zone));
                    setZones([...saved, ...serverZones.filter(z => !savedNames.has(z.zone))]);
                    setHasSaved(true);
                    setSavedAt(res.data.updatedAt || null);
                } else {
                    setZones([...serverZones]);
                    setHasSaved(false);
                }
            })
            .catch(() => setZones([...serverZones]))
            .finally(() => setLoading(false));
    }, [user, serverId, serverZones, zonesLoading]);

    const move = (fromIdx, toIdx) => {
        if (fromIdx === toIdx || toIdx < 0 || toIdx >= zones.length) return;
        const updated = [...zones];
        const [moved] = updated.splice(fromIdx, 1);
        updated.splice(toIdx, 0, moved);
        setZones(updated);
        setIsDirty(true);
    };

    /* ── Glisser-déposer ── */
    const handleDragStart = (e, idx) => {
        dragRef.current = idx;
        setDragging(idx);
        e.dataTransfer.effectAllowed = 'move';
    };
    const handleDragOver = (e, idx) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOver !== idx) setDragOver(idx);
    };
    const handleDrop = (e, dropIdx) => {
        e.preventDefault();
        if (dragRef.current !== null) move(dragRef.current, dropIdx);
        handleDragEnd();
    };
    const handleDragEnd = () => {
        dragRef.current = null;
        setDragging(null);
        setDragOver(null);
    };

    /* ── Saisie directe du rang ── */
    const applyRankEdit = (fromIdx, rawValue) => {
        const parsed = parseInt(rawValue, 10);
        if (!Number.isNaN(parsed)) move(fromIdx, Math.min(Math.max(parsed - 1, 0), zones.length - 1));
        setRankEdit(null);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.put(`/zone-preferences/${serverId}`, { preferences: zones });
            setHasSaved(true);
            setSavedAt(new Date().toISOString());
            setIsDirty(false);
            success(t('prefs.saved', 'Préférences enregistrées'));
        } catch {
            showError(t('prefs.saveError', 'Erreur lors de l\'enregistrement des préférences'));
        } finally {
            setSaving(false);
        }
    };

    const query = search.trim().toLowerCase();
    const isFiltering = query.length > 0;
    const displayed = zones
        .map((z, realIdx) => ({ ...z, realIdx }))
        .filter(z => !isFiltering || z.zone.toLowerCase().includes(query) || (z.donjon || '').toLowerCase().includes(query));

    if (loading) {
        return (
            <div className="c2-scope c2-admin-page">
                <div className="c2-loading">
                    <div className="c2-spinner" />
                    <p>{t('prefs.loading', 'Chargement de tes préférences...')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="c2-scope c2-admin-page">
            <PageHeader
                icon="list"
                title={t('prefs.title', 'Mes préférences de zones')}
                subtitle={`${server?.name ? `${server.name} · ` : ''}${t('prefs.zonesToRank', '{n} zones à classer', { n: zones.length })}`}
                actions={
                    <button type="button" className="c2-btn c2-btn--primary" onClick={handleSave} disabled={saving || (!isDirty && hasSaved)}>
                        <Icon name="save" size={18} />
                        {saving ? t('prefs.saving', 'Enregistrement...') : t('prefs.save', 'Enregistrer')}
                    </button>
                }
            />

            <div className={`prefs-status${isDirty ? ' is-dirty' : hasSaved ? ' is-saved' : ''}`}>
                <Icon name={isDirty ? 'pencil' : hasSaved ? 'check' : 'list'} size={18} />
                <span>
                    {isDirty
                        ? t('prefs.unsaved', 'Modifications non enregistrées')
                        : hasSaved
                            ? (savedAt
                                ? t('prefs.savedOn', 'Préférences enregistrées le {date} à {time}', formatDate(savedAt))
                                : t('prefs.saved', 'Préférences enregistrées'))
                            : t('prefs.notSavedYet', 'Tu n\'as pas encore enregistré de préférences : l\'ordre par défaut du serveur est utilisé.')}
                </span>
            </div>

            <section className="c2-card c2-section">
                <div className="c2-section__head">
                    <h2 className="c2-section__title"><Icon name="map" size={20} />{t('prefs.ranking', 'Classement')}</h2>
                    <input
                        type="search"
                        className="c2-input c2-input--sm prefs-search"
                        placeholder={t('prefs.searchPlaceholder', 'Rechercher une zone ou un donjon...')}
                        aria-label={t('prefs.searchLabel', 'Rechercher une zone')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                <p className="c2-section__desc">
                    {isFiltering
                        ? (displayed.length > 1
                            ? t('prefs.resultsHint', '{n} résultats · efface la recherche pour pouvoir glisser les lignes', { n: displayed.length })
                            : t('prefs.resultHint', '{n} résultat · efface la recherche pour pouvoir glisser les lignes', { n: displayed.length }))
                        : t('prefs.dragHint', 'Glisse les lignes, utilise les flèches ou clique sur un rang pour le saisir. #1 = ta zone préférée.')}
                </p>

                {displayed.length === 0 ? (
                    <div className="c2-empty">
                        <Icon name="search" size={32} />
                        <p>{t('prefs.noMatch', 'Aucune zone ne correspond à « {q} »', { q: search })}</p>
                    </div>
                ) : (
                    <div className="c2-table-wrap">
                        <table className="c2-table prefs-table">
                            <thead>
                                <tr>
                                    <th className="is-center">{t('prefs.rank', 'Rang')}</th>
                                    {!isFiltering && <th aria-label={t('prefs.move', 'Déplacer')}></th>}
                                    <th>{t('prefs.zone', 'Zone')}</th>
                                    <th>{t('prefs.dungeon', 'Donjon')}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayed.map(item => {
                                    const idx = item.realIdx;
                                    const rowClass = [
                                        dragging === idx && 'is-dragging',
                                        dragOver === idx && dragging !== idx && 'is-drag-over',
                                        idx < 3 && `is-top is-top--${idx + 1}`
                                    ].filter(Boolean).join(' ');
                                    return (
                                        <tr
                                            key={item.zone}
                                            className={rowClass || undefined}
                                            draggable={!isFiltering}
                                            onDragStart={e => handleDragStart(e, idx)}
                                            onDragOver={e => handleDragOver(e, idx)}
                                            onDrop={e => handleDrop(e, idx)}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <td className="is-center">
                                                {rankEdit?.realIdx === idx ? (
                                                    <input
                                                        type="number"
                                                        className="c2-input c2-input--sm prefs-rank-input"
                                                        min={1}
                                                        max={zones.length}
                                                        autoFocus
                                                        value={rankEdit.value}
                                                        onChange={e => setRankEdit({ realIdx: idx, value: e.target.value })}
                                                        onBlur={e => applyRankEdit(idx, e.target.value)}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter') applyRankEdit(idx, rankEdit.value);
                                                            if (e.key === 'Escape') setRankEdit(null);
                                                        }}
                                                        aria-label={t('prefs.newRankFor', 'Nouveau rang pour {zone}', { zone: item.zone })}
                                                    />
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="prefs-rank"
                                                        onClick={() => setRankEdit({ realIdx: idx, value: String(idx + 1) })}
                                                        title={t('prefs.clickToEditRank', 'Cliquer pour saisir un rang')}
                                                    >
                                                        #{idx + 1}
                                                    </button>
                                                )}
                                            </td>
                                            {!isFiltering && (
                                                <td className="prefs-grip" title={t('prefs.dragToMove', 'Glisser pour déplacer')}><Icon name="grip" size={16} /></td>
                                            )}
                                            <td><strong>{item.zone}</strong></td>
                                            <td className="c2-muted">{item.donjon || '—'}</td>
                                            <td className="is-num prefs-arrows">
                                                <button type="button" className="c2-icon-btn c2-icon-btn--sm" onClick={() => move(idx, idx - 1)} disabled={idx === 0} aria-label={t('prefs.moveUp', 'Monter {zone}', { zone: item.zone })}>
                                                    <Icon name="chevronUp" size={16} />
                                                </button>
                                                <button type="button" className="c2-icon-btn c2-icon-btn--sm" onClick={() => move(idx, idx + 1)} disabled={idx === zones.length - 1} aria-label={t('prefs.moveDown', 'Descendre {zone}', { zone: item.zone })}>
                                                    <Icon name="chevronDown" size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {isDirty && (
                <div className="prefs-savebar">
                    <span>{t('prefs.unsaved', 'Modifications non enregistrées')}</span>
                    <button type="button" className="c2-btn c2-btn--primary c2-btn--sm" onClick={handleSave} disabled={saving}>
                        <Icon name="save" size={16} />
                        {saving ? t('prefs.saving', 'Enregistrement...') : t('prefs.save', 'Enregistrer')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default PreferencesPage;
